import { API_TYPES, patch } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { z } from "zod";

import { useJobSearchAgreement } from "../../hooks/useJobSearchAgreement";

const SIGN_JOB_SEARCH_AGREEMENT_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/agentprofile/signJobSearchAgreement`;

const signJobSearchAgreementResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.signedJobSearchAgreement),
    id: z.string(),
    attributes: z.object({
      agreementVersion: z.string(),
      signedDate: z.string(),
      fullName: z.string().nullable(),
    }),
  }),
});

export type SignJobSearchAgreementResponse = z.infer<typeof signJobSearchAgreementResponseSchema>;

interface SignJobSearchAgreementParams {
  agreementVersion: number;
  fullName: string;
  forceReSignAgreement?: boolean;
}

export function useSignJobSearchAgreement(
  options: UseMutationOptions<
    Pick<Worker, "signedJobSearchAgreements">,
    Error,
    SignJobSearchAgreementParams
  > = {}
) {
  const worker = useDefinedWorker();
  const { refetchJobSearchAgreements } = useJobSearchAgreement({
    worker,
    enabled: false,
  });

  return useMutation({
    mutationKey: ["sign-job-search-agreement"] as const,
    mutationFn: async (params: SignJobSearchAgreementParams) => {
      const { agreementVersion, fullName, forceReSignAgreement = false } = params;

      const response = await patch({
        url: SIGN_JOB_SEARCH_AGREEMENT_URL,
        data: {
          data: {
            type: API_TYPES.signedJobSearchAgreement,
            attributes: {
              agreementVersion: `V${agreementVersion}`,
              fullName,
              forceReSignAgreement,
            },
          },
        },
        responseSchema: signJobSearchAgreementResponseSchema,
      });

      await refetchJobSearchAgreements();

      const { attributes } = response.data.data;
      return {
        signedJobSearchAgreements: [
          {
            agreementVersion: attributes.agreementVersion,
            signedDate: attributes.signedDate,
            fullName: attributes.fullName,
          },
        ],
      };
    },
    ...options,
  });
}
